import { asArray } from './promoPrimitives';
import { containersOf, findFreeRow, findMainRow, getCampaign, getOffer, negoRowsOf, newRow } from './promoModel';
import { autoContainer, productPaying, toUC, totalPaying } from './promoUnits';
import { isProductEditable } from './promoNego';

// Free products of an offer (#23521, spec RG-11) : a free line next to the paying line, in a green pill, without
// additional presence nor value, its volume still counted. Port of visitStep3-promo.js:4888-5155 ; the former
// `src/tests/promoValidation/promoValidation.js` copies now live here, as the production rules.

/** `getOfferProductConfig` (JS:4888) : the offer may list a product once per container. */
export function freeConfig(offer, productId) {
	const products = asArray(offer?.products).filter((p) => p.productId === productId);
	return {
		authGratuity: products.some((p) => p.authGratuity),
		pctGratuity: products.length ? Math.max(...products.map((p) => p.pctGratuity)) : 0,
		orderable: products.some((p) => p.orderable),
		freeOrderable: products.some((p) => p.freeOrderable),
	};
}

/**
 * Whether the free line can be added (`canAddFreeProduct`, JS:4957) : gratuity allowed on the product, and the paying
 * volume of the offer and of the product inside their min / max (UB in `displayUnit === 1` mode).
 */
export function canAddFreeProduct({ authGratuity, offerMinUC, offerMaxUC, totalPayingUC, productMinUC, productMaxUC, productPayingUC }) {
	if (!authGratuity) return false;
	if (offerMinUC && totalPayingUC < offerMinUC) return false;
	if (offerMaxUC && totalPayingUC > offerMaxUC) return false;
	if (productMinUC && productPayingUC < productMinUC) return false;
	if (productMaxUC && productPayingUC > productMaxUC) return false;
	return true;
}

/** Validates the free total of an offer against its MaxFreeQty (`isValid`). */
export function checkMaxFreeQty(totalFreeUC, maxFreeQty, displayUnit = 0) {
	if (!maxFreeQty || maxFreeQty <= 0) return null;
	if (totalFreeUC > maxFreeQty) return { error: displayUnit === 1 ? 'FreeUBMaxExceeded' : 'FreeUCMaxExceeded', maxFreeQty };
	return null;
}

/** Validates the free volume of a product against its gratuity % (pctGratuity is a percentage : 10 = 10 %, lesson 83). */
export function checkPctGratuity(freeVolumeUC, payingVolumeUC, pctGratuity, displayUnit = 0) {
	if (!pctGratuity) return null;
	const maxFreeUC = Math.round(((payingVolumeUC * pctGratuity) / 100) * 100) / 100;
	if (freeVolumeUC > maxFreeUC) return { error: displayUnit === 1 ? 'FreeUBPctExceeded' : 'FreeUCPctExceeded', maxFreeUC };
	return null;
}

/** Sums all the free UC of an offer (additional rows excluded). */
export function getTotalFreeUCForOffer(negos, campaignId, offerId) {
	return asArray(negos)
		.filter((o) => o.campaignId === campaignId && o.campaignOfferId === offerId && !o.additional && o.isFree)
		.reduce((sum, o) => sum + (o.volumeUC || 0), 0);
}

/** `canAddFreeProduct` on the snapshot, for a product line that accepts input. */
export function canAddFree(snapshot, ref, productId) {
	if (snapshot?.readonly) return false;
	const campaign = getCampaign(snapshot, ref.campaignId);
	const offer = getOffer(campaign, ref.campaignOfferId);
	if (!offer || !isProductEditable(snapshot, campaign, ref, productId)) return false;
	const rows = negoRowsOf(snapshot, ref);
	const ubMode = offer.displayUnit === 1;
	const offerProduct = asArray(offer.products).find((p) => p.productId === productId);
	return canAddFreeProduct({
		authGratuity: freeConfig(offer, productId).authGratuity,
		offerMinUC: offer.minUC,
		offerMaxUC: offer.maxUC,
		totalPayingUC: totalPaying(rows, ref, ubMode),
		productMinUC: offerProduct?.minUC || 0,
		productMaxUC: offerProduct?.maxUC || 0,
		productPayingUC: offerProduct ? productPaying(rows, ref, productId, ubMode) : 0,
	});
}

/** Adds the free line of a product, in the paying line's container (`addFreeProductHandler`, JS:5055). */
export function addFree(snapshot, ref, productId) {
	if (!canAddFree(snapshot, ref, productId)) return { ok: false, error: 'PromoFreeNotAllowed' };
	const rows = negoRowsOf(snapshot, ref).map((row) => ({ ...row }));
	if (findFreeRow(rows, ref, productId)) return { ok: false, error: 'PromoFreeExists' };
	const paying = findMainRow(rows, ref, productId);
	const free = newRow(ref, productId, { offerKey: ref.offerKey || '', isFree: true, local: !!ref.offerKey, productContainerId: paying ? paying.productContainerId : 0 });
	return { ok: true, ref, negotiated: [...rows, free] };
}

/** Removes the free line of a product. */
export function removeFree(snapshot, ref, productId) {
	const campaign = getCampaign(snapshot, ref.campaignId);
	if (!campaign || !isProductEditable(snapshot, campaign, ref, productId)) return { ok: false, error: 'PromoReadonly' };
	const rows = negoRowsOf(snapshot, ref);
	const free = findFreeRow(rows, ref, productId);
	return { ok: true, ref, negotiated: rows.filter((row) => row !== free).map((row) => ({ ...row })) };
}

/** Edits the free line (`updateFreeNegoHandler`, JS:5112) : volume / container / location / highlight ; its value is left untouched. */
export function applyFreeChange(snapshot, change) {
	const campaign = getCampaign(snapshot, change.campaignId);
	const offer = getOffer(campaign, change.campaignOfferId);
	const ref = { campaignId: change.campaignId, campaignOfferId: change.campaignOfferId, offerKey: change.offerKey || null };
	if (!campaign || !offer || !isProductEditable(snapshot, campaign, ref, change.productId)) return { ok: false, error: 'PromoReadonly' };
	const rows = negoRowsOf(snapshot, ref).map((row) => ({ ...row }));
	const free = findFreeRow(rows, ref, change.productId);
	if (!free) return { ok: false, error: 'PromoUnknownTarget' };
	const value = Number(change.value) || 0;
	if (change.field === 'volume') {
		free.volume = value;
		free.productContainerId = autoContainer(containersOf(snapshot, offer, change.productId), free.volume, free.productContainerId);
		free.volumeUC = toUC(snapshot, change.productId, free.volume, free.productContainerId);
	}
	if (change.field === 'container') {
		free.productContainerId = value;
		free.volumeUC = toUC(snapshot, change.productId, free.volume, free.productContainerId);
	}
	if (change.field === 'location') free.locationPromoId = value;
	if (change.field === 'highlight') free.locationHighlightId = value;
	return { ok: true, ref, negotiated: rows };
}

export const freeRowOf = (snapshot, ref, productId) => findFreeRow(negoRowsOf(snapshot, ref), ref, productId);
